import { Controller } from '@hotwired/stimulus';
import { getCsrfToken } from '../../bundles/utilities/csrf';

export default class extends Controller {
  static targets = [
    'input',
    'filename',
    'errorText',
    'validIcon',
    'spinnerIcon',
  ];

  static values = {
    validationUrl: String,
    errorText: String,
    valid: Boolean,
    processing: Boolean,
  };

  get files() {
    return this.inputTarget.files;
  }

  initialize() {
    this.valueChanged = this.valueChanged.bind(this);
  }

  connect() {
    this.inputTarget.addEventListener('change', this.valueChanged);
  }

  disconnect() {
    this.inputTarget.removeEventListener('change', this.valueChanged);
  }

  async valueChanged(event) {
    this.rejectMultipleFiles();
    this.validValue = false;
    this.errorTextValue = '';

    // Files were deselected
    if (this.files.length === 0) {
      this.filenameTarget.classList.add('hidden');
      return;
    }

    this.filenameTarget.classList.remove('hidden');
    this.filenameTarget.innerText = this.files[0].name;

    if (this.hasValidationUrlValue) {
      await this.validate();
    }
  }

  async validate() {
    if (!this.hasValidationUrlValue) {
      throw new Error('No URL was given, the controller does not know where to send the file for validation');
    }
    this.processingValue = true;
    this.rejectMultipleFiles();

    const form = new FormData();
    form.set('authenticity_token', getCsrfToken());
    form.set('file', this.files[0]);

    const response = await fetch(this.validationUrlValue, {
      method: 'POST',
      body: form,
      credentials: 'include',
      headers: {
        authenticity_token: getCsrfToken(),
      },
    });

    if (!response.ok && response.status !== 422) {
      this.processingValue = false;
      this.errorTextValue = 'Something went wrong when validating your file. Please try again';
      throw new Error('Validation on the server failed');
    }

    const json = await response.json();

    if (response.status === 422 || (json.errors && json.errors.length > 0)) {
      this.errorTextValue = `<ul>${json.errors.map((err) => `<li>${err}</li>`).join('')}</ul>`;
    } else {
      this.validValue = true;
    }
    this.processingValue = false;
  }

  rejectMultipleFiles() {
    if (this.files.length > 1) {
      throw new Error('Not Implemented: File input does not know how to handle multiple files');
    }
  }

  processingValueChanged() {
    if (this.processingValue) {
      this.spinnerIconTarget.classList.remove('hidden');
    } else {
      this.spinnerIconTarget.classList.add('hidden');
    }
  }

  validValueChanged() {
    if (this.validValue) {
      this.validIconTarget.classList.remove('hidden');
    } else {
      this.validIconTarget.classList.add('hidden');
    }
  }

  errorTextValueChanged() {
    if (this.hasErrorTextValue && this.errorTextValue === '') {
      this.errorTextTarget.classList.add('hidden');
      this.errorTextTarget.innerHTML = '';
    } else if (this.hasErrorTextValue) {
      this.errorTextTarget.classList.remove('hidden');
      this.errorTextTarget.innerHTML = this.errorTextValue;
    }
  }
}
